import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, Download, X, Loader2 } from "lucide-react";
import { useConsentLogs, fetchAllFilteredConsentLogs } from "@/hooks/superadmin/useConsentLogs";
import { useWithdrawConsent } from "@/hooks/superadmin/useWithdrawConsent";
import PaginationControls from "@/components/designer-grid/PaginationControls";
import { useToast } from "@/hooks/use-toast";
import { exportConsentLogsToCSV, downloadCSV } from "@/utils/exportConsentLogs";
import { getConsentGroupKey, ConsentLog } from "@/utils/consentLogGrouping";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import PolicyVersions from "./PolicyVersions";

const CONSENT_TYPE_LABELS: Record<ConsentLog["consent_type"], string> = {
  privacy_policy: "Privacy",
  terms_acceptance: "Terms",
  cookies: "Cookies",
  marketing: "Marketing",
};

const CONSENT_TYPE_ORDER: ConsentLog["consent_type"][] = [
  "privacy_policy",
  "terms_acceptance",
  "cookies",
  "marketing",
];

const ConsentLogs = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "approved" | "withdrawn">("all");
  const [consentTypeFilter, setConsentTypeFilter] = useState<string>("all");
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef({
    isDragging: false,
    startX: 0,
    scrollLeft: 0,
  });
  const [isDragScrollable, setIsDragScrollable] = useState(false);
  
  const { toast } = useToast();
  const { withdrawConsent, isWithdrawing } = useWithdrawConsent({
    onSuccess: () => {
      setSelectedRows(new Set());
      setShowWithdrawDialog(false);
    },
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, consentTypeFilter]);

  // Fetch consent logs
  const {
    data: consentLogs,
    pagination,
    stats,
    isLoading,
    error,
  } = useConsentLogs({
    page: currentPage,
    pageSize,
    searchTerm: debouncedSearchTerm,
    statusFilter,
    consentTypeFilter: consentTypeFilter !== "all" ? consentTypeFilter : undefined,
  });

  // Use stats if available (when no filters), otherwise calculate from current page
  const approvedCount = stats?.totalApproved ?? consentLogs?.filter((log) => log.granted).length ?? 0;
  const withdrawnCount = stats?.totalWithdrawn ?? consentLogs?.filter((log) => !log.granted).length ?? 0;

  const groupedConsentLogs = useMemo(() => {
    if (!consentLogs || consentLogs.length === 0) return [];

    const consentTypeOrder = new Map(
      CONSENT_TYPE_ORDER.map((type, index) => [type, index])
    );

    const groups = new Map<
      string,
      {
        key: string;
        logs: ConsentLog[];
        logIds: string[];
        consentTypeSet: Set<ConsentLog["consent_type"]>;
        primaryLog: ConsentLog;
      }
    >();

    consentLogs.forEach((log) => {
      const key = getConsentGroupKey(log);
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, {
          key,
          logs: [log],
          logIds: [log.id],
          consentTypeSet: new Set([log.consent_type]),
          primaryLog: log,
        });
        return;
      }

      existing.logs.push(log);
      existing.logIds.push(log.id);
      existing.consentTypeSet.add(log.consent_type);
    });

    return Array.from(groups.values()).map((group) => {
      const consentTypes = Array.from(group.consentTypeSet).sort((a, b) => {
        return (consentTypeOrder.get(a) ?? 999) - (consentTypeOrder.get(b) ?? 999);
      });
      const consentTypeLabels = consentTypes.map(
        (type) => CONSENT_TYPE_LABELS[type] || type
      );
      const allGranted = group.logs.every((log) => log.granted);
      const allWithdrawn = group.logs.every((log) => !log.granted);
      const status = allGranted ? "approved" : allWithdrawn ? "withdrawn" : "mixed";

      return {
        ...group,
        consentTypes,
        consentTypeLabels,
        status,
      };
    });
  }, [consentLogs]);

  // Extract language from user agent (simple implementation)
  const extractLanguage = (userAgent: string | null): string => {
    if (!userAgent) return "-";
    // Simple regex to extract language from Accept-Language or user agent
    const langMatch = userAgent.match(/(?:[a-z]{2}(?:-[A-Z]{2})?)/i);
    return langMatch ? langMatch[0].toLowerCase() : "en";
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format status badge
  const getStatusBadge = (status: "approved" | "withdrawn" | "mixed") => {
    if (status === "approved") {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          <span className="mr-1">•</span> Approved
        </Badge>
      );
    }
    if (status === "withdrawn") {
      return (
        <Badge className="bg-red-100 text-red-800 border-red-200">
          <span className="mr-1">•</span> Withdrawn
        </Badge>
      );
    }

    return (
      <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
        <span className="mr-1">•</span> Mixed
      </Badge>
    );
  };

  const toggleRowSelection = (id: string) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const toggleAllSelection = () => {
    if (groupedConsentLogs.length === 0) return;
    if (selectedRows.size === groupedConsentLogs.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(groupedConsentLogs.map((row) => row.key)));
    }
  };

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!dragStateRef.current.isDragging) return;
      const container = tableScrollRef.current;
      if (!container) return;

      const deltaX = event.clientX - dragStateRef.current.startX;
      container.scrollLeft = dragStateRef.current.scrollLeft - deltaX;
      event.preventDefault();
    };

    const handleMouseUp = () => {
      if (!dragStateRef.current.isDragging) return;
      dragStateRef.current.isDragging = false;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  useEffect(() => {
    const container = tableScrollRef.current;
    if (!container) return;

    const updateScrollable = () => {
      setIsDragScrollable(container.scrollWidth > container.clientWidth);
    };

    updateScrollable();
    window.addEventListener("resize", updateScrollable);
    return () => window.removeEventListener("resize", updateScrollable);
  }, [consentLogs]);

  const handleTableMouseDown = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    if (event.button !== 0) return;
    const target = event.target as HTMLElement;
    if (target.closest("button, input, select, textarea, a, [role='button']")) {
      return;
    }
    const container = tableScrollRef.current;
    if (!container) return;

    dragStateRef.current.isDragging = true;
    dragStateRef.current.startX = event.clientX;
    dragStateRef.current.scrollLeft = container.scrollLeft;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
    event.preventDefault();
  };

  const selectedConsentLogIds = useMemo(() => {
    if (selectedRows.size === 0) return [];

    const ids: string[] = [];
    groupedConsentLogs.forEach((group) => {
      if (selectedRows.has(group.key)) {
        ids.push(...group.logIds);
      }
    });
    return ids;
  }, [groupedConsentLogs, selectedRows]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Fetch all filtered consent logs
      const allLogs = await fetchAllFilteredConsentLogs({
        searchTerm: debouncedSearchTerm,
        statusFilter,
        consentTypeFilter: consentTypeFilter !== "all" ? consentTypeFilter : undefined,
      });

      if (allLogs.length === 0) {
        toast({
          title: "No data to export",
          description: "There are no consent logs matching the current filters.",
          variant: "destructive",
        });
        return;
      }

      // Convert to CSV
      const csvContent = exportConsentLogsToCSV(allLogs);

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `consent-logs-${timestamp}.csv`;

      // Download the CSV file
      downloadCSV(csvContent, filename);

      toast({
        title: "Export successful",
        description: `Exported ${allLogs.length} consent log${allLogs.length !== 1 ? "s" : ""} to CSV.`,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to export consent logs";
      
      toast({
        title: "Export failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Tabs defaultValue="consent-logs" className="flex flex-col flex-1 min-h-0">
      <TabsList className="w-fit">
        <TabsTrigger value="consent-logs">Consent Logs</TabsTrigger>
        <TabsTrigger value="policy-versions">Policy Versions</TabsTrigger>
      </TabsList>

      <TabsContent
        value="consent-logs"
        className="mt-4 flex flex-col data-[state=active]:flex-1 data-[state=active]:min-h-0 space-y-4"
      >
        {/* Header with stats and actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 flex-shrink-0">
        <div className="flex flex-wrap items-center gap-4">
          <Badge className="bg-green-100 text-green-800 border-green-200 px-3 py-1.5 text-sm font-medium">
            {approvedCount} Approved
          </Badge>
          <Badge className="bg-red-100 text-red-800 border-red-200 px-3 py-1.5 text-sm font-medium">
            {withdrawnCount} Withdrawn
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            disabled={selectedRows.size === 0 || isWithdrawing}
            onClick={() => setShowWithdrawDialog(true)}
          >
            <X className="mr-2 h-4 w-4" />
            Withdraw
          </Button>
          <Button 
            variant="default" 
            disabled={isExporting}
            onClick={handleExport}
          >
            {isExporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Export
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Search and filter */}
      <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Q Search log"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as "all" | "approved" | "withdrawn")}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="withdrawn">Withdrawn</SelectItem>
          </SelectContent>
        </Select>
        <Select value={consentTypeFilter} onValueChange={setConsentTypeFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="privacy_policy">Privacy Policy</SelectItem>
            <SelectItem value="terms_acceptance">Terms Acceptance</SelectItem>
            <SelectItem value="cookies">Cookies</SelectItem>
            <SelectItem value="marketing">Marketing</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-md border overflow-hidden flex-1 min-h-0 flex flex-col">
        <Table
          wrapperRef={tableScrollRef}
          wrapperProps={{
            className: `flex-1 min-h-0 ${
              isDragScrollable ? "cursor-grab active:cursor-grabbing" : ""
            }`,
            onMouseDown: handleTableMouseDown,
          }}
        >
          <TableHeader className="sticky top-0 bg-white z-10 shadow-sm border-b">
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={
                    groupedConsentLogs.length > 0 &&
                    selectedRows.size === groupedConsentLogs.length
                  }
                  onCheckedChange={toggleAllSelection}
                />
              </TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Bundle</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>IP</TableHead>
              <TableHead>Browser</TableHead>
              <TableHead>OS</TableHead>
              <TableHead>Device</TableHead>
              <TableHead>Language</TableHead>
              <TableHead>Method</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                  Loading consent logs...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8 text-red-600">
                  Error loading consent logs: {error.message}
                </TableCell>
              </TableRow>
            ) : !consentLogs || consentLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                  No consent logs found
                </TableCell>
              </TableRow>
            ) : (
              groupedConsentLogs.map((group) => {
                const log = group.primaryLog;
                return (
                <TableRow key={group.key}>
                  <TableCell>
                    <Checkbox
                      checked={selectedRows.has(group.key)}
                      onCheckedChange={() => toggleRowSelection(group.key)}
                    />
                  </TableCell>
                  <TableCell>{log.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-nowrap gap-2 whitespace-nowrap">
                      {group.consentTypeLabels.map((label) => (
                        <Badge
                          key={`${group.key}-${label}`}
                          className="bg-slate-100 text-slate-700 border border-slate-200 shrink-0"
                        >
                          {label}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{formatDate(log.timestamp)}</TableCell>
                  <TableCell>{getStatusBadge(group.status)}</TableCell>
                  <TableCell className="font-mono text-sm">
                    {log.ip_address || "-"}
                  </TableCell>
                  <TableCell className="text-sm">
                    {log.browser_name ? (
                      <div>
                        <div className="font-medium">{log.browser_name}</div>
                        {log.browser_version && (
                          <div className="text-xs text-muted-foreground">{log.browser_version}</div>
                        )}
                      </div>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {log.os_name ? (
                      <div>
                        <div className="font-medium">{log.os_name}</div>
                        {log.os_version && (
                          <div className="text-xs text-muted-foreground">{log.os_version}</div>
                        )}
                      </div>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {log.device_type ? (
                      <div>
                        <div className="font-medium capitalize">{log.device_type}</div>
                        {log.device_brand && log.device_model && (
                          <div className="text-xs text-muted-foreground">
                            {log.device_brand} {log.device_model}
                          </div>
                        )}
                      </div>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>{extractLanguage(log.user_agent)}</TableCell>
                  <TableCell>{log.source_form || "-"}</TableCell>
                </TableRow>
              );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <PaginationControls
          pagination={pagination}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Withdraw Confirmation Dialog */}
      <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <DialogContent className="bg-white rounded-lg md:max-w-xl p-8">
          <DialogHeader>
            <DialogTitle>Withdraw Consent</DialogTitle>
            <DialogDescription>
              Are you sure you want to withdraw {selectedRows.size} consent{selectedRows.size !== 1 ? 's' : ''}? 
              This will withdraw all consent types in the selected event{selectedRows.size !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              This action cannot be undone. The withdrawal will be recorded with the current timestamp, 
              IP address, and user agent for compliance purposes.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowWithdrawDialog(false)}
              disabled={isWithdrawing}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                withdrawConsent(selectedConsentLogIds);
              }}
              disabled={isWithdrawing}
            >
              {isWithdrawing ? 'Withdrawing...' : `Withdraw ${selectedRows.size} Consent${selectedRows.size !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </TabsContent>

      <TabsContent
        value="policy-versions"
        className="mt-4 flex flex-col data-[state=active]:flex-1 data-[state=active]:min-h-0"
      >
        <div className="flex-1 min-h-0 overflow-auto pr-1">
          <PolicyVersions />
        </div>
      </TabsContent>
    </Tabs>
  );
};

export default ConsentLogs;
